import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format } from 'date-fns';
import { formatGatewayConfigVersion } from '@cnapse/shared/gateway-config-version.js';
import { AppButton, AppIconButton } from '../design-system';

export interface GatewayConfigPushHistoryRow {
  configVersion: string;
  status: string;
  pushedAt?: string | null;
  scopeId?: string | null;
}

export interface GatewayConfigPushHistoryTableProps {
  title?: string;
  history: GatewayConfigPushHistoryRow[];
  isLoading?: boolean;
  isFetching?: boolean;
  onRefresh?: () => void;
  showResendForVersion?: string | null;
  onResend?: (version: string) => void;
  resendPending?: boolean;
  formatVersion?: (version: string) => string;
  showScopeColumn?: boolean;
  showViewConfigColumn?: boolean;
  onViewConfig?: (version: string) => void;
  emptyMessage?: string;
}

export function GatewayConfigPushHistoryTable({
  title = 'Push History',
  history,
  isLoading = false,
  isFetching = false,
  onRefresh,
  showResendForVersion = null,
  onResend,
  resendPending = false,
  formatVersion = formatGatewayConfigVersion,
  showScopeColumn = false,
  showViewConfigColumn = false,
  onViewConfig,
  emptyMessage = 'No config pushes recorded.',
}: GatewayConfigPushHistoryTableProps) {
  const colSpan = 4 + (showScopeColumn ? 1 : 0) + (showViewConfigColumn ? 1 : 0);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
          {title}
        </Typography>
        {onRefresh && (
          <AppButton
            size="small"
            onClick={() => void onRefresh()}
            disabled={isFetching}
            startIcon={
              isFetching ? (
                <CircularProgress size={12} color="inherit" />
              ) : (
                <RefreshIcon style={{ fontSize: 24 }} />
              )
            }
            sx={{ textTransform: 'none', minWidth: 40 }}
            aria-label="Refresh push history"
          />
        )}
      </Box>
      <Table size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '4px' }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell>Version</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Pushed</TableCell>
            {showScopeColumn && <TableCell>Site</TableCell>}
            {showViewConfigColumn && <TableCell>Config</TableCell>}
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {history.map((row) => (
            <TableRow key={row.configVersion}>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                {formatVersion(row.configVersion)}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={row.status}
                  color={row.status === 'applied' ? 'success' : 'default'}
                  sx={{ height: 20, fontSize: 11 }}
                />
              </TableCell>
              <TableCell>
                {row.pushedAt ? format(new Date(row.pushedAt), 'yyyy-MM-dd HH:mm') : '—'}
              </TableCell>
              {showScopeColumn && (
                <TableCell sx={{ fontSize: 12 }}>{row.scopeId ?? '—'}</TableCell>
              )}
              {showViewConfigColumn && onViewConfig && (
                <TableCell>
                  <Tooltip title="View config">
                    <AppIconButton
                      size="small"
                      onClick={() => onViewConfig(row.configVersion)}
                      aria-label={`View config version ${row.configVersion}`}
                    >
                      <VisibilityIcon fontSize="small" />
                    </AppIconButton>
                  </Tooltip>
                </TableCell>
              )}
              <TableCell align="right">
                {onResend && row.configVersion === showResendForVersion ? (
                  <AppButton
                    size="small"
                    onClick={() => onResend(row.configVersion)}
                    disabled={resendPending}
                    sx={{ textTransform: 'none' }}
                  >
                    Resend MQTT
                  </AppButton>
                ) : (
                  '—'
                )}
              </TableCell>
            </TableRow>
          ))}
          {history.length === 0 && !isLoading && (
            <TableRow>
              <TableCell colSpan={colSpan} align="center" sx={{ py: 3 }}>
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
          {isLoading && history.length === 0 && (
            <TableRow>
              <TableCell colSpan={colSpan} align="center" sx={{ py: 3 }}>
                <CircularProgress size={20} />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
}
